import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface InvRow {
  id: string; location: string;
  qty_on_hand: string; min_stock: string; max_stock: string | null;
  avg_unit_cost_sar: string | null; last_movement_at: string | null;
  part_id: string; part_code: string; part_name: string; part_number: string | null; category: string | null;
}

const fmtSar = (s: string | null) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function InventoryPage() {
  const [rows, setRows] = useState<InvRow[]>([]);
  const [low, setLow] = useState<InvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjustFor, setAdjustFor] = useState<{ partId: string; partName: string; location: string; current: number } | null>(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustCost, setAdjustCost] = useState('');
  const [adjustReason, setAdjustReason] = useState('manual_adjustment');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [all, lowR] = await Promise.all([
        apiClient.get<InvRow[]>('/api/v1/maintenance/inventory'),
        apiClient.get<InvRow[]>('/api/v1/maintenance/inventory/low-stock'),
      ]);
      setRows(Array.isArray(all.data) ? all.data : []);
      setLow(Array.isArray(lowR.data) ? lowR.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function submitAdjust() {
    if (!adjustFor || !adjustQty) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/api/v1/maintenance/inventory/${adjustFor.partId}/adjust`, {
        location: adjustFor.location, quantity: Number(adjustQty), reason: adjustReason,
        unitCostSar: adjustCost ? Number(adjustCost) : undefined,
      });
      setAdjustFor(null); setAdjustQty(''); setAdjustCost('');
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> Inventory</div>
          <div className="w-24" />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        {low.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <strong>{low.length}</strong> parts below min stock — order soon.
          </div>
        )}
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : rows.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No inventory rows yet — adjust stock from a part to start.</div>
        : <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr><th className="text-left px-3 py-2">Part</th><th className="text-left px-3 py-2">Number</th><th className="text-left px-3 py-2">Location</th>
                  <th className="text-right px-3 py-2">On hand</th><th className="text-right px-3 py-2">Min</th>
                  <th className="text-right px-3 py-2">Avg cost</th><th className="text-right px-3 py-2">Last</th>
                  <th className="text-right px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const onHand = Number(r.qty_on_hand);
                  const min = Number(r.min_stock ?? 0);
                  return (
                    <tr key={r.id} className={'border-b border-border last:border-b-0 ' + (onHand <= min ? 'bg-amber-500/5' : '')}>
                      <td className="px-3 py-2">{r.part_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.part_number ?? '—'}</td>
                      <td className="px-3 py-2 text-xs">{r.location}</td>
                      <td className="px-3 py-2 text-right font-mono">{onHand}</td>
                      <td className="px-3 py-2 text-right text-xs">{min}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{r.avg_unit_cost_sar ? fmtSar(r.avg_unit_cost_sar) : '—'}</td>
                      <td className="px-3 py-2 text-right text-xs">{r.last_movement_at ? new Date(r.last_movement_at).toLocaleDateString('en-GB') : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => setAdjustFor({ partId: r.part_id, partName: r.part_name, location: r.location, current: onHand })}>Adjust</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
      </main>

      <Sheet open={!!adjustFor} onOpenChange={(o) => !o && setAdjustFor(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Adjust inventory</SheetTitle>
            <SheetDescription>{adjustFor?.partName} · {adjustFor?.location} · current: {adjustFor?.current}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            <div className="space-y-1">
              <Label>Quantity (negative to remove)</Label>
              <Input type="number" step="0.5" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)} placeholder="+10 or -2" />
            </div>
            <div className="space-y-1">
              <Label>Unit cost (SAR, optional — updates avg)</Label>
              <Input type="number" step="0.01" min={0} value={adjustCost} onChange={(e) => setAdjustCost(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Reason</Label>
              <Input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
            </div>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAdjustFor(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitAdjust} disabled={submitting || !adjustQty}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save adjustment</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
